import React, { useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import _ from 'lodash-es';
import moment from 'moment';
import { formatToCurrency } from "../../../../helper/form";
import { isAuthorizedCustomFieldToUser } from "../../../../helper/custom_field/helper";

const LaboralBulkLoadHiringTable = ({
  data,
  dataKey = "uniqueId",
  employeesSelect = [],
  branchSelect = [],
  customFieldsHeaders = [],
  currentUser,
  actionsRenderer,
  onDataChange,
  title,
  showErrors = false,
  isExistHiringId
}) => {
  const onChangeCellValues = (value, row, key, customField) => {
    if (!value) {
      return;
    }

    if (customField?.data_type == 'currency') {
      value = formatToCurrency(value);
    }

    if (customField?.data_type == 'date') {
      // se convierte a formato DD/MM/YYYY para que al cargar los campos
      // los vuelva a transformar a YYYY-MM-DD y asi no se pierde el formato para mostrarlo
      value = moment(value, "YYYY-MM-DD").locale('es').format('DD/MM/YYYY');
    }

    if (onDataChange) {
      onDataChange(value, row, key, customField);
    }
  };

  const customFieldSelectOptions = useCallback((listOptions) => {
    return listOptions.map((option) => ({
      label: option,
      value: option
    }));
  }, []);

  const employeeOptions = () => {
    return _.map(employeesSelect, employee => ({
      label: employee.name,
      value: `${employee.identifier} / ${employee.name}`
    }));
  };

  const getEmployeeLabel = (employeeId) => {
    const employee = _.find(employeesSelect, { identifier: employeeId });
    return employee ? employee.name : employeeId;
  };

  const branchOptions = () => {
    return _.map(branchSelect, branch => ({
      label: branch.name,
      value: `${branch.identifier} / ${branch.name}`
    }));
  };

  const getBranchLabel = (branchId) => {
    const branch = _.find(branchSelect, { identifier: branchId });
    return branch ? branch.name : branchId;
  };

  const renderEmployeeEditor = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={employeeOptions()}
        onChange={(e) => {
          options.editorCallback(e.value);
          onChangeCellValues(e.value, options.rowData, "employee_id");
        }}
        placeholder="Seleccionar trabajador"
      />
    );
  };

  const renderBranchEditor = (options) => {
    return (
      <Dropdown
        value={options.value}
        options={branchOptions()}
        onChange={(e) => {
          options.editorCallback(e.value);
          onChangeCellValues(e.value, options.rowData, "branch_id");
        }}
        placeholder="Seleccionar sucursal"
      />
    );
  };

  const renderStartDateEditor = (options) => {
    const value = options.value || '';
    const isInvalid = showErrors && value === '';

    return (
      <div>
        <InputText
          type="date"
          value={value}
          onChange={(e) => {
            options.editorCallback(e.target.value);
            onChangeCellValues(e.target.value, options.rowData, "start_date");
          }}
          className={isInvalid ? 'p-invalid' : ''}
          placeholder="Seleccionar fecha de inicio"
        />
        {isInvalid && <small className="p-error">No debe ser vacío</small>}
      </div>
    );
  };

  const renderEndDateEditor = (options) => {
    const value = options.value || '';
    const isInvalid = showErrors && options.rowData.start_date && value && options.rowData.start_date > value;

    return (
      <div>
        <InputText
          type="date"
          value={value}
          onChange={(e) => {
            options.editorCallback(e.target.value);
            onChangeCellValues(e.target.value, options.rowData, "end_date");
          }}
          className={isInvalid ? 'p-invalid' : ''}
          placeholder="Seleccionar fecha de término"
        />
        {isInvalid && <small className="p-error">No puede ser menor que la fecha de inicio</small>}
      </div>
    );
  };

  const renderCustomFieldEditor = (options, customField) => {
    if (customField.data_type === "list") {
      const optionsList = customFieldSelectOptions(customField.list_options);
      return (
        <Dropdown
          value={options.value}
          options={optionsList}
          onChange={(e) => {
            options.editorCallback(e.value);
            onChangeCellValues(e.value, options.rowData, customField.code, customField);
          }}
          disabled={!isAuthorizedCustomFieldToUser(customField, currentUser)}
          placeholder={`Seleccionar ${customField.code}`}
        />
      );
    } else {
      return (
        <InputText
          value={options.value}
          onChange={(e) => {
            options.editorCallback(e.target.value);
            onChangeCellValues(e.target.value, options.rowData, customField.code, customField);
          }}
          disabled={!isAuthorizedCustomFieldToUser(customField, currentUser)}
          placeholder={`Ingrese ${customField.code}`}
        />
      );
    }
  };

  const startDateBodyTemplate = (rowData) => {
    if (!rowData.start_date) return '';
    return moment(rowData.start_date, "YYYY-MM-DD").locale('es').format('DD-MM-YYYY');
  };

  const endDateBodyTemplate = (rowData) => {
    if (!rowData.end_date) return '';
    return moment(rowData.end_date, "YYYY-MM-DD").locale('es').format('DD-MM-YYYY');
  };

  const renderActions = (rowData) => {
    if (actionsRenderer) {
      return actionsRenderer(rowData);
    }
    return null;
  };

  const editingRows = _.reduce(
    data,
    (accumulator, hiring) => ({
      ...accumulator,
      [hiring[dataKey]]: true
    }),
    {}
  );

  return (
    <div className="row pt-2">
      {title && <div className="mb-2">{title}</div>}

      <DataTable
        value={data}
        editMode="row"
        editingRows={editingRows}
        onRowEditChange={() => { }} // Si no se define entonces editingRows no funciona.
        size="small"
        dataKey={dataKey}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
        emptyMessage="No existen registros"
        scrollable
        scrollHeight="600px"
        className="p-datatable-striped"
      >
        <Column
          field="hiring_id"
          header="Identificador"
          sortable
          filter
          showAddButton={false}
          filterPlaceholder="Buscar identificador"
        />
        <Column
          field="employee_id"
          header="Trabajador"
          editor={(options) => renderEmployeeEditor(options)}
          body={(rowData) => getEmployeeLabel(rowData.employee_id?.split(" /")[0])}
          sortable
          filter
          showAddButton={false}
          filterPlaceholder="Buscar trabajador"
          style={{ width: '300px' }}
        />
        <Column
          field="branch_id"
          header="Sucursal"
          editor={(options) => renderBranchEditor(options)}
          body={(rowData) => getBranchLabel(rowData.branch_id?.split(" /")[0])}
          sortable
          filter
          showAddButton={false}
          filterPlaceholder="Buscar sucursal"
          style={{ width: '300px' }}
        />
        <Column
          field="start_date"
          header="Fecha Inicio"
          showAddButton={false}
          body={startDateBodyTemplate}
          editor={(options) => renderStartDateEditor(options)}
          sortable
          filter
          filterPlaceholder="Buscar fecha de inicio"
        />
        <Column
          field="end_date"
          header="Fecha Término"
          showAddButton={false}
          body={endDateBodyTemplate}
          editor={(options) => renderEndDateEditor(options)}
          sortable
          filter
          filterPlaceholder="Buscar fecha de término"
        />
        {_.map(customFieldsHeaders, (customField) => {
          const isAuthorizedCustomField = isAuthorizedCustomFieldToUser(customField, currentUser);

          return (
            <Column
              key={customField.code}
              field={customField.code}
              header={customField.code}
              editor={(options) => renderCustomFieldEditor(options, customField)}
              sortable={isAuthorizedCustomField}
              filter={isAuthorizedCustomField}
              showAddButton={false}
              filterPlaceholder={`Buscar ${customField.code}`}
            />
          );
        })}
        {actionsRenderer && (
          <Column
            field="row"
            header="Acciones"
            body={renderActions}
            style={{ width: '150px' }}
          />
        )}
      </DataTable>
    </div>
  );
};

export default LaboralBulkLoadHiringTable;
